import React, { useState, useEffect } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Button, Text, Select } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';
import TokenStatusIndicator from '../../common/TokenStatusIndicator';

export function AdobeCommerceCredentialsModal({ open, onClose, onSave, config = {} }) {
  // Support two modes: oauth2 (Adobe I/O), token (Integration token)
  const [formData, setFormData] = useState({
    deploymentType: 'paas', // 'paas' | 'saas'
    authMethod: 'token', // 'token' | 'ims_oauth2'
    baseUrl: '',
    // PaaS fields
    token: '',
    // SaaS fields
    region: 'na1',
    clientId: '',
    clientSecret: '',
    scopes: 'openid,AdobeID,read_organizations',
    // Webhook fields
    webhookPublicKeyPem: '',
    webhookPrivateKey: '',
    webhookEnabled: false,
  });
  const [validation, setValidation] = useState({ running: false, ok: null, message: '' });
  const canSave = (() => {
    const urlOk = String(formData.baseUrl || '').trim().length > 0;
    if (formData.deploymentType === 'paas' && formData.authMethod === 'token') {
      // Allow saving if we have a URL and either a real token or existing token (****)
      return urlOk && (String(formData.token || '').trim().length > 0 || config.tokenEnc);
    }
    if (formData.deploymentType === 'saas' && formData.authMethod === 'ims_oauth2') {
      // Allow saving if we have URL, clientId, and either real secret or existing secret (****)
      return urlOk && String(formData.clientId || '').trim().length > 0 && 
             (String(formData.clientSecret || '').trim().length > 0 || config.ims?.clientSecretEnc);
    }
    return false;
  })();

  // Update form data when config changes
  useEffect(() => {
    if (config && Object.keys(config).length > 0) {
      setFormData(prev => {
        const updated = {
          ...prev,
          ...config
        };
        if (Array.isArray(updated.scopes)) {
          updated.scopes = updated.scopes.join(',');
        }
        return updated;
      });
    }
  }, [config]);

  const handleSave = () => {
    const payload = { ...formData };
    
    if (payload.deploymentType === 'paas' && payload.authMethod === 'token') {
      const saveData = { 
        deploymentType: 'paas',
        authMethod: 'token',
        baseUrl: payload.baseUrl,
        webhooks: payload.webhookEnabled ? {
          enabled: true,
          publicKeyPem: payload.webhookPublicKeyPem,
          privateKey: payload.webhookPrivateKey
        } : { enabled: false }
      };
      
      // Only include token if it's not the placeholder
      if (payload.token && payload.token !== '****') {
        saveData.token = payload.token;
      }
      
      onSave && onSave('adobe-paas', saveData);
    } else if (payload.deploymentType === 'saas' && payload.authMethod === 'ims_oauth2') {
      const saveData = { 
        deploymentType: 'saas',
        authMethod: 'ims_oauth2',
        baseUrl: payload.baseUrl, 
        region: payload.region,
        clientId: payload.clientId,
        scopes: (payload.scopes || '').split(/[,\s]+/).filter(Boolean),
        webhooks: payload.webhookEnabled ? {
          enabled: true,
          publicKeyPem: payload.webhookPublicKeyPem,
          privateKey: payload.webhookPrivateKey
        } : { enabled: false }
      };
      
      // Only include clientSecret if it's not the placeholder
      if (payload.clientSecret && payload.clientSecret !== '****') {
        saveData.clientSecret = payload.clientSecret;
      }
      
      onSave && onSave('adobe-saas', saveData);
    }
    onClose();
  };

  const handleValidate = async () => {
    try {
      setValidation({ running: true, ok: null, message: '' });
      const cleaned = { ...formData };
      // Treat masked placeholders as empty (require re-entry)
      ['consumerKey','consumerSecret','accessToken','accessTokenSecret','clientSecret','token'].forEach((k)=>{
        if (String(cleaned[k] || '').trim() === '****') cleaned[k] = '';
      });
      if (cleaned.deploymentType === 'saas' && cleaned.authMethod === 'ims_oauth2') {
        cleaned.scopes = (cleaned.scopes || '')
          .split(/[,\s]+/)
          .map(s => s.trim())
          .filter(Boolean);
      }
      const resp = await configApi.validateCredentials('adobe', cleaned);
      if (resp?.success) setValidation({ running: false, ok: true, message: resp.message || 'Valid' });
      else setValidation({ running: false, ok: false, message: resp?.error || 'Invalid' });
    } catch {
      setValidation({ running: false, ok: false, message: 'Validation failed' });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Adobe Commerce Credentials"
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: !canSave,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" fontWeight="bold">Adobe Commerce Credentials</Text>
            {validation.ok === true && <Badge tone="success">Valid</Badge>}
            {validation.ok === false && <Badge tone="critical">{validation.message || 'Invalid'}</Badge>}
          </InlineStack>
          <Select
            label="Deployment Type"
            options={[
              { label: 'PaaS (Platform as a Service)', value: 'paas' },
              { label: 'SaaS (Software as a Service)', value: 'saas' }
            ]}
            value={formData.deploymentType}
            onChange={(value) => { 
              const newAuthMethod = value === 'paas' ? 'token' : 'ims_oauth2';
              setFormData({ ...formData, deploymentType: value, authMethod: newAuthMethod }); 
              setValidation({ running: false, ok: null, message: '' }); 
            }}
          />
          
          <Select
            label="Authentication Method"
            options={
              formData.deploymentType === 'paas' 
                ? [{ label: 'Token (PaaS, non-expiring token)', value: 'token' }]
                : [{ label: 'IMS (SaaS, JWT)', value: 'ims_oauth2' }]
            }
            value={formData.authMethod}
            onChange={(value) => { setFormData({ ...formData, authMethod: value }); setValidation({ running: false, ok: null, message: '' }); }}
            disabled={true}
            helpText={formData.deploymentType === 'paas' ? 'Uses Adobe Commerce Integration Tokens' : 'Uses Adobe Identity Management Services with JWT tokens'}
          />
          <TextField
            label="Base URL"
            value={formData.baseUrl || ''}
            onChange={(value) => { setFormData({ ...formData, baseUrl: value }); if (!validation.running) setValidation({ running: false, ok: null, message: '' }); }}
            placeholder="https://your-store.com"
            autoComplete="off"
            helpText="Your Adobe Commerce store's base URL"
          />
          {/* PaaS Authentication Fields */}
          {formData.deploymentType === 'paas' && formData.authMethod === 'token' && (
            <>
              <TextField
                label="Integration Token"
                type="password"
                value={formData.token || ''}
                onChange={(value) => { setFormData({ ...formData, token: value }); setValidation({ running: false, ok: null, message: '' }); }}
                helpText="Adobe Commerce Integration token (non-expiring)"
                autoComplete="off"
              />
            </>
          )}

          {/* SaaS Authentication Fields */}
          {formData.deploymentType === 'saas' && formData.authMethod === 'ims_oauth2' && (
            <>
              <Select
                label="IMS Region"
                options={[
                  { label: 'North America (NA1)', value: 'na1' },
                  { label: 'Europe (EU1)', value: 'eu1' },
                  { label: 'Asia Pacific (AP1)', value: 'ap1' }
                ]}
                value={formData.region}
                onChange={(value) => { setFormData({ ...formData, region: value }); setValidation({ running: false, ok: null, message: '' }); }}
                helpText="Select your Adobe IMS region"
              />
              <TextField
                label="Client ID"
                value={formData.clientId || ''}
                onChange={(value) => { setFormData({ ...formData, clientId: value }); setValidation({ running: false, ok: null, message: '' }); }}
                helpText="From Adobe Developer Console"
                autoComplete="off"
              />
              <TextField
                label="Client Secret"
                type="password"
                value={formData.clientSecret || ''}
                onChange={(value) => { setFormData({ ...formData, clientSecret: value }); setValidation({ running: false, ok: null, message: '' }); }}
                helpText="From Adobe Developer Console"
                autoComplete="off"
              />
              <TextField
                label="Scopes"
                value={formData.scopes || ''}
                onChange={(value) => { setFormData({ ...formData, scopes: value }); setValidation({ running: false, ok: null, message: '' }); }}
                helpText="Comma or space separated (e.g., openid,AdobeID,read_organizations)"
                autoComplete="off"
              />
            </>
          )}
          {/* Webhook Configuration Section */}
          <div style={{ marginTop: '20px', padding: '16px', border: '1px solid #e1e3e5', borderRadius: '4px' }}>
            <Text variant="headingSm" fontWeight="bold">Webhook Configuration (Optional)</Text>
            <div style={{ marginTop: '12px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.webhookEnabled}
                  onChange={(e) => setFormData({ ...formData, webhookEnabled: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                Enable webhook signature verification
              </label>
            </div>
            
            {formData.webhookEnabled && (
              <>
                <div style={{ marginTop: '12px' }}>
                  <TextField
                    label="Webhook Public Key (PEM)"
                    value={formData.webhookPublicKeyPem || ''}
                    onChange={(value) => { setFormData({ ...formData, webhookPublicKeyPem: value }); if (!validation.running) setValidation({ running: false, ok: null, message: '' }); }}
                    multiline={6}
                    autoComplete="off"
                    helpText="Paste the Adobe-generated webhook public key PEM for signature verification"
                  />
                </div>
                <div style={{ marginTop: '12px' }}>
                  <TextField
                    label="Webhook Private Key (PEM) - Optional"
                    type="password"
                    value={formData.webhookPrivateKey || ''}
                    onChange={(value) => { setFormData({ ...formData, webhookPrivateKey: value }); if (!validation.running) setValidation({ running: false, ok: null, message: '' }); }}
                    multiline={4}
                    autoComplete="off"
                    helpText="Private key for certificate exchange (if required)"
                  />
                </div>
              </>
            )}
          </div>
          
          {/* Token Status Indicator */}
          <div style={{ marginTop: '20px' }}>
            <Text variant="headingSm" fontWeight="bold">Token Status</Text>
            <TokenStatusIndicator provider="adobe" />
          </div>
          
          <TextField
            label="Test URL (optional)"
            value={formData.testUrl || ''}
            onChange={(value) => { setFormData({ ...formData, testUrl: value }); if (!validation.running) setValidation({ running: false, ok: null, message: '' }); }}
            placeholder="https://your-store.com/rest/V1/store/storeConfigs"
            autoComplete="off"
            helpText="Leave empty to use /rest/V1/store/storeConfigs"
          />
          <InlineStack align="end" gap="none">
            <Button onClick={handleValidate} loading={validation.running} variant="secondary">Validate</Button>
          </InlineStack>
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
}
